import { css, useTheme } from "@emotion/react"
import React from "react"
import Select, {
  CSSObjectWithLabel,
  ControlProps,
  GroupBase,
  OptionProps,
  Props,
} from "react-select"
import { BodySubtextNormal, BodySubtextSemibold } from "../../typography/text"
import { fieldStyle } from "./TextField"

type OwnProps = {
  innerRef?: any
  errorMessage?: string
  label?: string
  helperText?: string
} & Props

export const SelectField = ({
  innerRef,
  errorMessage,
  label,
  helperText,
  ...rest
}: OwnProps) => {
  const theme = useTheme()
  const labelStyle = css`
    color: ${theme.colors.primary};
    margin-bottom: 8px !important;
    ${rest.required &&
    `
      &:after {
        color: ${theme.colors.errorDark};
        content: "*";
        margin-left: 2px;
      }
    `}
  `

  const subTextStyle = css`
    margin-top: 8px !important;
  `

  const getCustomControlStyle = (
    state: ControlProps<unknown, boolean, GroupBase<unknown>>
  ): CSSObjectWithLabel => ({
    padding: "8px",
    display: "flex",
    flexWrap: "nowrap",
    height: "56px",
    ":hover": {
      borderColor: state.isFocused
        ? theme.colors.ctaMain
        : errorMessage
        ? theme.colors.errorMedium
        : theme.colors.ctaMain,
    },
    outline: state.isFocused
      ? `1px solid ${theme.colors.borderMedium}`
      : "none",
    border: `1px solid ${
      state.isFocused
        ? theme.colors.borderMedium
        : errorMessage
        ? theme.colors.errorDark
        : rest.value
        ? theme.colors.successDark
        : theme.colors.greyBorder
    }`,
  })

  const getCustomOptionStyle = (
    state: OptionProps<unknown, boolean, GroupBase<unknown>>
  ): CSSObjectWithLabel => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
    padding: "8px",
    fontFamily: "Inter400",
    fontSize: "14px",
    borderRadius: "4px",
    cursor: state.isDisabled ? "default" : "pointer",
    color: state.isDisabled ? theme.colors.greyBorder : theme.colors.primary,
    backgroundColor: state.isSelected
      ? theme.colors.ctaLight
      : state.isFocused
      ? theme.colors.whiteHover
      : theme.colors.white,
    ":hover": {
      backgroundColor: state.isDisabled
        ? theme.colors.white
        : state.isSelected
        ? theme.colors.ctaLight
        : theme.colors.whiteHover,
    },
  })

  const menuListCustomStyle: CSSObjectWithLabel = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "8px",
  }

  const multiValueRemoveCustomStyle: CSSObjectWithLabel = {
    borderRadius: "4px",
    backgroundColor: theme.colors.ctaMain,
    display: "flex",
    flexDirection: "row",
    textAlign: "center",
    alignItems: "center",
    marginRight: "4px",
    padding: "4px 6px",
    background: theme.colors.whiteHover,
    border: `1px solid ${theme.colors.greyBorder}`,
  }

  const multiValueLabelCustomStyle: CSSObjectWithLabel = {
    fontFamily: "Inter400",
    fontSize: "12px",
    marginRight: "4px",
  }
  const multiValueCustomStyle: CSSObjectWithLabel = {
    borderRadius: "4px",
    backgroundColor: theme.colors.ctaMain,
    display: "flex",
    flexDirection: "row",
    textAlign: "center",
    alignItems: "center",
    marginRight: "4px",
    padding: "4px 6px",
    background: theme.colors.whiteHover,
    border: `1px solid ${theme.colors.greyBorder}`,
  }

  const inputCustomStyles: CSSObjectWithLabel = {
    fontFamily: "Inter400",
    fontSize: "14px",
  }

  const singleValueCustomStyles: CSSObjectWithLabel = {
    fontFamily: "Inter400",
    fontSize: "14px",
  }

  const menuPortalCustomStyle: CSSObjectWithLabel = {
    zIndex: 100000,
  }

  return (
    <div css={fieldStyle}>
      {label && (
        <BodySubtextSemibold css={labelStyle}>{label}</BodySubtextSemibold>
      )}
      <Select
        ref={innerRef}
        styles={{
          control: (baseStyle, state) => ({
            ...baseStyle,
            ...getCustomControlStyle(state),
          }),
          multiValue: () => multiValueCustomStyle,
          multiValueRemove: () => multiValueRemoveCustomStyle,
          input: (baseStyle) => ({
            ...baseStyle,
            ...inputCustomStyles,
          }),
          singleValue: (baseStyle) => ({
            ...baseStyle,
            ...singleValueCustomStyles,
          }),
          multiValueLabel: () => multiValueLabelCustomStyle,
          menuPortal: (baseStyle) => ({
            ...baseStyle,
            ...menuPortalCustomStyle,
          }),
          menuList: (baseStyle) => ({
            ...baseStyle,
            ...menuListCustomStyle,
          }),
          option: (baseStyle, state) => ({
            ...baseStyle,
            ...getCustomOptionStyle(state),
          }),
        }}
        {...rest}
      />
      {!errorMessage && helperText && (
        <BodySubtextNormal color={theme.colors.secondary2} css={subTextStyle}>
          {helperText}
        </BodySubtextNormal>
      )}
      {errorMessage && (
        <BodySubtextNormal color={theme.colors.errorDark} css={subTextStyle}>
          {errorMessage}
        </BodySubtextNormal>
      )}
    </div>
  )
}
